import {useCallback, useMemo, useRef, useState} from 'react';
import {
  useQuotesFirst,
  useQuotingAvailability,
} from '@/matic/features/quoting/hooks/useQuotingAvailability';
import {createPortal} from 'react-dom';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {
  ChevronRight,
  Home,
  Inbox,
  LayoutList,
  LibraryBig,
  FileSpreadsheet,
  PackageSearch,
  Settings,
} from 'lucide-react';
import {useHoverOrFocusDisclosure} from '@/matic/features/_shared/hooks/useHoverOrFocusDisclosure';
import {useSidebarStore} from './sidebarStore';
import {useNavConfig, type NavItemConfig} from './navConfig';
import {GlobalCreateFab} from './GlobalCreateFab';
import {usePendingApprovals} from '@/matic/features/vault/approvals/hooks/usePendingApprovals';
import {useMyAssignmentsQuery} from '@/matic/features/training/my-training/hooks/useMyAssignmentsQuery';
import {useMyPendingAttestationsQuery} from '@/matic/features/training/my-training/hooks/useMyPendingAttestationsQuery';
import {useMyPendingInstructorSignoffsQuery} from '@/matic/features/training/my-training/hooks/useMyPendingInstructorSignoffsQuery';
import {useTriageCount} from '@/matic/features/relay/hooks/useTriageCount';
import {useLibraryPages} from '@/matic/features/relay/hooks/useLibraryPages';
import {
  encodeScopeParam,
  scopeKeyOf,
} from '@/matic/features/relay/lib/scope-utils';
import {useUiMode} from '@/matic/features/_shared/hooks/useUiMode';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {useProcurementAvailability} from '@/matic/features/procurement/hooks/useProcurementAvailability';
import {
  RELAY_CONFIG_PATH,
  RELAY_CONFIG_SECTIONS,
  resolveRelayConfigSection,
} from '@/matic/features/relay/components/config/relayConfigSections';

import UserProfile from '../UserProfile';
import {cn} from '@/matic/lib/utils/cn';

// ---------------------------------------------------------------------------
// CollapsedRailIcon — a single collapsed-rail nav icon with optional badge.
// ---------------------------------------------------------------------------
function CollapsedRailIcon({
  to,
  icon: Icon,
  label,
  isActive,
  badge,
}: {
  to: string;
  icon: React.ComponentType<{className?: string}>;
  label: string;
  isActive: boolean;
  badge?: number;
}) {
  return (
    <Link
      to={to}
      title={label}
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
      }}
      className={cn(
        'relative flex h-10 w-10 items-center justify-center rounded-md no-underline transition-colors',
        isActive
          ? 'bg-primary-500/10 text-primary-500'
          : 'text-sidebar-fg hover:bg-sidebar-hover hover:text-white'
      )}
    >
      <Icon className="h-5 w-5" />
      {badge !== undefined && badge > 0 && (
        <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-bold text-white shadow-sm ring-2 ring-sidebar-bg">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
}

const COLLAPSED_FLYOUT_GAP_PX = 4;
const COLLAPSED_SIDEBAR_WIDTH_PX = 64;
// `w-64` (256px) mirrors EXPANDED_SIDEBAR_WIDTH_PX in sidebar-layout-constants.ts
// — kept as a Tailwind class here since this is a static style, not a position
// calculation; update both if the rail width ever changes.
const RELAY_FLYOUT_SURFACE_CLASS =
  'fixed z-50 w-64 overflow-y-auto rounded-lg border border-primary-500 bg-sidebar-bg py-1 shadow-[0_18px_48px_rgba(0,0,0,0.42)]';

const SETTINGS_FLYOUT_HEIGHT_PX = 172;

function CollapsedSettingsQuickNav({isActive}: {isActive: boolean}) {
  const {search} = useLocation();
  const iconRef = useRef<HTMLAnchorElement>(null);
  const [flyoutPos, setFlyoutPos] = useState<{
    top: number;
    left: number;
    bridgeLeft: number;
    bridgeWidth: number;
  } | null>(null);
  const activeSection = resolveRelayConfigSection(
    new URLSearchParams(search).get('section')
  );

  const closeFlyout = useCallback(() => {
    setFlyoutPos(null);
  }, []);

  const openFlyout = useCallback(() => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (!rect) return;
    const left = COLLAPSED_SIDEBAR_WIDTH_PX + COLLAPSED_FLYOUT_GAP_PX;
    const top = Math.max(8, rect.bottom - SETTINGS_FLYOUT_HEIGHT_PX);
    setFlyoutPos({
      top,
      left,
      bridgeLeft: rect.right,
      bridgeWidth: Math.max(0, left - rect.right),
    });
  }, []);

  const {triggerHandlers, flyoutHandlers} = useHoverOrFocusDisclosure(
    openFlyout,
    closeFlyout
  );

  return (
    <>
      <Link
        ref={iconRef}
        to={RELAY_CONFIG_PATH}
        title="Settings"
        aria-label="Settings"
        aria-haspopup="true"
        aria-expanded={flyoutPos !== null}
        className="relative flex h-10 w-10 items-center justify-center rounded-md text-sidebar-fg/55 no-underline transition-colors hover:bg-sidebar-hover hover:text-white"
        {...triggerHandlers}
        onClick={(event) => {
          event.stopPropagation();
          closeFlyout();
        }}
      >
        <Settings className="h-5 w-5" />
      </Link>

      {flyoutPos &&
        createPortal(
          <>
            {flyoutPos.bridgeWidth > 0 && (
              <div
                className="fixed z-50"
                style={{
                  top: flyoutPos.top,
                  left: flyoutPos.bridgeLeft,
                  width: flyoutPos.bridgeWidth,
                  height: SETTINGS_FLYOUT_HEIGHT_PX,
                }}
                onMouseEnter={flyoutHandlers.onMouseEnter}
                onMouseLeave={flyoutHandlers.onMouseLeave}
              />
            )}
            <div
              className={RELAY_FLYOUT_SURFACE_CLASS}
              style={{top: flyoutPos.top, left: flyoutPos.left}}
              {...flyoutHandlers}
            >
              <div className="border-b border-sidebar-border/70 px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-sidebar-fg/50">
                Settings
              </div>
              <div className="py-1">
                {RELAY_CONFIG_SECTIONS.map((section) => {
                  const Icon = section.icon;
                  const itemActive = isActive && activeSection === section.id;
                  return (
                    <Link
                      key={section.id}
                      to={`${RELAY_CONFIG_PATH}?section=${section.id}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        closeFlyout();
                      }}
                      className={cn(
                        'flex items-center gap-2 px-3 py-2 text-sm font-medium no-underline transition-colors hover:bg-sidebar-hover hover:text-white',
                        itemActive ? 'text-primary-500' : 'text-sidebar-fg'
                      )}
                    >
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{section.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
}

// ---------------------------------------------------------------------------
// FlyoutNavItem — icon button that reveals a portal-based flyout on hover.
// Used for OBJECTS in Otto mode so recent objects are reachable from the
// collapsed sidebar without needing to expand it.
// ---------------------------------------------------------------------------
type FlyoutNavItemProps = {
  item: NavItemConfig;
  isActive: boolean;
};

function FlyoutNavItem({item, isActive}: FlyoutNavItemProps) {
  const {pathname} = useLocation();
  const iconRef = useRef<HTMLAnchorElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [flyoutPos, setFlyoutPos] = useState<{top: number} | null>(null);

  const Icon = item.icon;

  const openFlyout = useCallback(() => {
    if (!iconRef.current) return;
    const rect = iconRef.current.getBoundingClientRect();
    // Anchor the flyout just below the icon with a small gap.
    setFlyoutPos({top: rect.bottom + 8});
  }, []);

  const closeFlyout = useCallback(() => {
    setFlyoutPos(null);
  }, []);

  const {triggerHandlers, flyoutHandlers} = useHoverOrFocusDisclosure(
    openFlyout,
    closeFlyout,
    {openDelayMs: 400, closeDelayMs: 80}
  );

  const hasChildren = Boolean(item.children && item.children.length > 0);

  if (!Icon || !item.path) return null;

  return (
    <>
      <Link
        ref={iconRef}
        to={item.path}
        aria-haspopup={hasChildren ? 'true' : undefined}
        aria-expanded={hasChildren ? flyoutPos !== null : undefined}
        className={cn(
          'relative flex h-10 w-10 items-center justify-center rounded-md no-underline transition-colors',
          isActive
            ? 'bg-primary-500/10 text-primary-500'
            : 'text-sidebar-fg hover:bg-sidebar-hover hover:text-white'
        )}
        onClick={closeFlyout}
        {...triggerHandlers}
      >
        <Icon className="h-5 w-5" />
      </Link>

      {flyoutPos &&
        hasChildren &&
        createPortal(
          <div
            ref={flyoutRef}
            className="fixed z-50 w-64 rounded-lg border border-primary-500/30 bg-sidebar-bg px-3 py-2"
            style={{
              left: 16,
              top: flyoutPos.top,
              boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
            }}
            {...flyoutHandlers}
          >
            {/* Section label — matches the "VIEW" label style in the profile popout */}
            <div className="border-b border-primary-500/30 px-4 pb-2 pt-1">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                {item.label}
              </p>
            </div>
            <div className="py-1">
              {item.children?.map((child) => {
                if (!child.path) return null;
                const childActive =
                  pathname === child.path ||
                  pathname.startsWith(child.path + '/');
                return (
                  <Link
                    key={child.path}
                    to={child.path}
                    onClick={closeFlyout}
                    className={cn(
                      'flex w-full items-center truncate rounded px-4 py-2 text-sm no-underline transition-colors',
                      childActive
                        ? 'text-primary-500'
                        : 'text-white hover:bg-white/10'
                    )}
                  >
                    {child.label}
                  </Link>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

// ---------------------------------------------------------------------------
// CollapsedPagesFlyout — Otto-mode "Pages" icon that reveals the user's
// saved Library pages (pins) on hover, so they're reachable without
// expanding the sidebar. Mirrors FlyoutNavItem's portal/positioning pattern.
// ---------------------------------------------------------------------------
const PAGES_FLYOUT_GAP_PX = 8;

function CollapsedPagesFlyout() {
  const navigate = useNavigate();
  const iconRef = useRef<HTMLButtonElement>(null);
  const [flyoutPos, setFlyoutPos] = useState<{top: number} | null>(null);
  const {pages, activeScopeKey} = useLibraryPages();

  const isActive = pages.some(
    (page) => activeScopeKey !== '' && activeScopeKey === scopeKeyOf(page.items)
  );

  const openFlyout = useCallback(() => {
    if (!iconRef.current) return;
    const rect = iconRef.current.getBoundingClientRect();
    setFlyoutPos({top: rect.bottom + PAGES_FLYOUT_GAP_PX});
  }, []);

  const closeFlyout = useCallback(() => {
    setFlyoutPos(null);
  }, []);

  const {triggerHandlers, flyoutHandlers} = useHoverOrFocusDisclosure(
    openFlyout,
    closeFlyout,
    {openDelayMs: 400, closeDelayMs: 80}
  );

  return (
    <>
      <button
        ref={iconRef}
        type="button"
        aria-label="Pages"
        aria-haspopup="true"
        aria-expanded={flyoutPos !== null}
        className={cn(
          'relative flex h-10 w-10 items-center justify-center rounded-md transition-colors',
          isActive
            ? 'bg-primary-500/10 text-primary-500'
            : 'text-sidebar-fg hover:bg-sidebar-hover hover:text-white'
        )}
        {...triggerHandlers}
        onClick={(event) => {
          event.stopPropagation();
          closeFlyout();
        }}
      >
        <LayoutList className="h-5 w-5" />
      </button>

      {flyoutPos &&
        createPortal(
          <div
            className="fixed z-50 w-64 rounded-lg border border-primary-500/30 bg-sidebar-bg px-3 py-2"
            style={{
              left: 16,
              top: flyoutPos.top,
              boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
            }}
            {...flyoutHandlers}
          >
            <div className="border-b border-primary-500/30 px-4 pb-2 pt-1">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                Pages
              </p>
            </div>
            <div className="py-1">
              {pages.length === 0 ? (
                <p className="px-4 py-2 text-sm text-sidebar-fg/55">
                  No pages yet — pin one from Library.
                </p>
              ) : (
                pages.map((page) => {
                  const pageActive =
                    activeScopeKey !== '' &&
                    activeScopeKey === scopeKeyOf(page.items);
                  return (
                    <button
                      key={page.id}
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        closeFlyout();
                        navigate(
                          `/library?scope=${encodeScopeParam(page.items)}`
                        );
                      }}
                      className={cn(
                        'flex w-full items-center truncate rounded px-4 py-2 text-left text-sm transition-colors',
                        pageActive
                          ? 'text-primary-500'
                          : 'text-white hover:bg-white/10'
                      )}
                    >
                      <span className="truncate">{page.name}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

// ---------------------------------------------------------------------------
// CollapsedSidebar
// ---------------------------------------------------------------------------

export default function CollapsedSidebar() {
  // The AES shell follows the tenant's engine; its frame only renders once that's known.
  const quotesFirst = useQuotesFirst() === true;
  const location = useLocation();
  const {pathname} = location;
  const openSidebar = useSidebarStore((state) => state.open);

  const navSections = useNavConfig();
  const tenantId = useRequiredTenantId();
  const {mode: uiMode} = useUiMode();
  const showProcurement = useProcurementAvailability();
  // Runtime: the tenant's quoting engine, not the build (quotes-first has its own entry).
  const {enabled: showQuotes} = useQuotingAvailability();
  const onHome = pathname === '/home' || pathname.startsWith('/home/');
  const onLibraryLanding =
    (pathname === '/library' || pathname.startsWith('/library/')) &&
    !new URLSearchParams(location.search).has('scope');

  // Clicking empty space anywhere in the collapsed rail (not on a nav
  // icon/link/button) expands the sidebar, matching the chevron button.
  // The target === currentTarget check ensures this only fires when the
  // click lands directly on the container, not when it bubbles up from a
  // child Link/button.
  const handleEmptySpaceClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (event.target === event.currentTarget) openSidebar();
    },
    [openSidebar]
  );

  // Fetch pending tasks count
  const {data: approvals} = usePendingApprovals(tenantId);
  const {data: assignmentsData} = useMyAssignmentsQuery();
  const triageCount = useTriageCount();
  // Pending instructor actions — drives the TRAINING badge in the
  // collapsed view (the Sign-offs leaf isn't visible when collapsed,
  // so the parent has to carry the count).
  const {data: pendingInstructorSignoffs} =
    useMyPendingInstructorSignoffsQuery();
  const {data: pendingSecondaryAttestations} = useMyPendingAttestationsQuery();

  // Inject pending count badges into nav items
  const sectionsWithBadges = useMemo(() => {
    const approvalCount = approvals?.total ?? 0;
    const assignmentCount =
      assignmentsData?.items.filter((a) => a.status !== 'completed').length ??
      0;
    const totalPendingCount = approvalCount + assignmentCount;
    const signoffsCount =
      (pendingInstructorSignoffs?.length ?? 0) +
      (pendingSecondaryAttestations?.length ?? 0);

    return navSections.map((section) => ({
      ...section,
      items: section.items.map((item) => {
        if (item.path === '/notifications' && totalPendingCount > 0) {
          return {
            ...item,
            badge: totalPendingCount > 99 ? '99+' : String(totalPendingCount),
          };
        }

        if (item.path === '/inbox' && triageCount > 0) {
          return {
            ...item,
            badge: triageCount > 99 ? '99+' : String(triageCount),
          };
        }

        if (item.label === 'TRAINING' && signoffsCount > 0) {
          return {
            ...item,
            badge: signoffsCount > 99 ? '99+' : String(signoffsCount),
          };
        }

        return item;
      }),
    }));
  }, [
    navSections,
    approvals,
    assignmentsData,
    triageCount,
    pendingInstructorSignoffs,
    pendingSecondaryAttestations,
  ]);

  return (
    <div
      className="flex h-full w-full flex-col"
      onClick={handleEmptySpaceClick}
    >
      {/* Header: M centered (expands the rail, same as clicking anywhere else
          on it); chevron overlays right edge as a second, explicit affordance
          for the same action. */}
      <div className="relative flex h-16 w-full shrink-0 items-center justify-center border-b border-sidebar-border">
        <button
          type="button"
          aria-label="Expand sidebar"
          onClick={openSidebar}
          className="flex h-full w-full items-center justify-center text-xl font-bold text-white transition-colors hover:text-primary-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
        >
          M
        </button>
        <button
          type="button"
          aria-label="Expand sidebar"
          onClick={openSidebar}
          className="absolute inset-y-0 right-0 flex w-6 items-center justify-center text-sidebar-fg/60 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
        >
          <ChevronRight className="h-5 w-5 scale-y-125" />
        </button>
      </div>

      {/* Nav Items */}
      <nav
        className={cn(
          'flex flex-1 flex-col items-center gap-6 overflow-y-auto px-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-sidebar-active/50 hover:[&::-webkit-scrollbar-thumb]:bg-sidebar-active [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5',
          // The expanded Relay rail has a 40px tenant/search strip followed
          // by 6px of rail padding. Reserve the same 46px above the compact
          // icons so Home, Triage, and Library stay on the same baseline.
          uiMode === 'otto' ? 'pb-4 pt-[46px]' : 'py-4'
        )}
        onClick={handleEmptySpaceClick}
      >
        {uiMode === 'otto' ? (
          // Relay mode keeps the primary expanded-rail destinations reachable
          // as icons: Home, Triage, Library, then Pages.
          <div
            className="flex w-full flex-col items-center gap-2"
            onClick={handleEmptySpaceClick}
          >
            {quotesFirst && (
              <CollapsedRailIcon
                to="/"
                icon={FileSpreadsheet}
                label="Quotes"
                isActive={pathname === '/' || pathname.startsWith('/quotes')}
              />
            )}
            {!quotesFirst && (
              <CollapsedRailIcon
                to="/home"
                icon={Home}
                label="Home"
                isActive={onHome}
              />
            )}
            <CollapsedRailIcon
              to="/inbox"
              icon={Inbox}
              label="Inbox"
              isActive={pathname.startsWith('/inbox')}
              badge={triageCount}
            />
            {showProcurement && !quotesFirst && (
              <CollapsedRailIcon
                to="/procurement"
                icon={PackageSearch}
                label="Procurement"
                isActive={pathname.startsWith('/procurement')}
              />
            )}
            {showQuotes && !quotesFirst && (
              <CollapsedRailIcon
                to="/quotes"
                icon={FileSpreadsheet}
                label="Quotes"
                isActive={pathname.startsWith('/quotes')}
              />
            )}
            <CollapsedRailIcon
              to="/library"
              icon={LibraryBig}
              label="Library"
              isActive={onLibraryLanding}
            />
            <CollapsedPagesFlyout />
          </div>
        ) : (
          sectionsWithBadges.map((section, index) => (
            <div
              key={index}
              className="flex w-full flex-col items-center gap-2"
              onClick={handleEmptySpaceClick}
            >
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = item.path
                  ? pathname === item.path ||
                    (pathname.startsWith(item.path + '/') &&
                      !/^\/[a-z]/i.test(pathname.slice(item.path.length)))
                  : false;

                if (!Icon) return null;
                if (!item.path) return null;

                // Items with children get a hover flyout in the collapsed state.
                if (item.children && item.children.length > 0) {
                  return (
                    <FlyoutNavItem
                      key={item.label}
                      item={item}
                      isActive={isActive}
                    />
                  );
                }

                return (
                  <Link
                    key={item.label}
                    to={item.path}
                    className={cn(
                      'relative flex h-10 w-10 items-center justify-center rounded-md no-underline transition-colors',
                      isActive
                        ? 'bg-primary-500/10 text-primary-500'
                        : 'text-sidebar-fg hover:bg-sidebar-hover hover:text-white'
                    )}
                    title={item.label}
                  >
                    <Icon className="h-5 w-5" />

                    {/* Badge */}
                    {item.badge && (
                      <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-bold text-white shadow-sm ring-2 ring-sidebar-bg">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))
        )}
      </nav>

      <div className="flex flex-col items-center gap-3 border-t border-sidebar-border py-3">
        {uiMode === 'otto' && (
          <CollapsedSettingsQuickNav
            isActive={pathname.startsWith(RELAY_CONFIG_PATH)}
          />
        )}
        <UserProfile variant="sidebar" collapsed={true} />
      </div>

      <GlobalCreateFab />
    </div>
  );
}
